import React, { Fragment, useContext, useRef } from 'react';
import { observer } from 'mobx-react';
import { generatePath, Redirect } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Route, Switch } from 'react-router-dom';

import Overview from './Overview';

import Users from './users/Users';
import User from './users/User';

import {
  ROUTE_SETTINGS,
  ROUTE_SETTINGS_ENTITY,
  ROUTE_SETTINGS_SUB,
  ROUTE_SETTINGS_USERS,
  ROUTE_SETTINGS_USERS_ADD,
  ROUTE_SETTINGS_USERS_USER,
  ROUTE_SETTINGS_USERS_USER_EDIT,
  ROUTE_SETTINGS_USERS_USER_ROLES,
  ROUTE_SETTINGS_ORGANISATIONS,
  ROUTE_SETTINGS_ORGANISATIONS_ORG,
  ROUTE_SETTINGS_ORGANISATIONS_ADD,
  ROUTE_SETTINGS_ORGANISATIONS_ORG_EDIT,
  ROUTE_SETTINGS_GLOBAL,
  ROUTE_SETTINGS_GLOBAL_EDIT,
  ROUTE_SETTINGS_BENCHMARKS,
  ROUTE_SETTINGS_BENCHMARKS_BENCHMARK,
  ROUTE_SETTINGS_BENCHMARKS_BENCHMARK_TEST,
  ROUTE_SETTINGS_TESTSETS_TESTSET,
  ROUTE_SETTINGS_TESTSETS,
  ROUTE_SETTINGS_BENCHMARKS_BENCHMARK_TEST_EDIT,
  ROUTE_SETTINGS_USAGE,
  ROUTE_SETTINGS_DEMODATA,
  ROUTE_PERSONS_ENTITY,
  ROUTE_SETTINGS_PROFILE,
  ROUTE_SETTINGS_PROFILE_MFA,
  ROUTE_SETTINGS_PROFILE_EDIT,
  ROUTE_SETTINGS_EXPORT_DATA,
  ROUTE_SETTINGS_SEARCH_USERS
} from 'routes/RouteList';

import messages from 'messages';
import Organisations from './organisations/Organisations';
import Organisation from './organisations/Organisation';
import GlobalSettings from './global/GlobalSettings';
import Benchmarks from './benchmarks/Benchmarks';
import Benchmark from './benchmarks/Benchmark';
import BenchmarkTest from './benchmarks/BenchmarkTest';
import TestSets from './testsets/TestSets';
import TestSet from './testsets/TestSet';
import { PreviewModalProvider } from 'contexts/PreviewModalContext';
import { TestSetContextProvider } from 'contexts/TestSetContext';
import { TestsProvider } from 'contexts/TestsContext';
import { StoreContext } from 'index';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import Usage from 'containers/pages/settings/usage/Usage';
import DemoData from 'containers/pages/settings/demodata/DemoData';
import NoAccessPanel from 'containers/pages/auth/NoAccessPanel';
import MfaSetup from 'containers/pages/settings/profile/MfaSetup';
import Profile from 'containers/pages/settings/profile';
import ExportData from 'containers/pages/settings/export/ExportData';
import {
  createEntityStore,
  EntityStoreProvider
} from 'containers/pages/persons/store/EntityStoreContext';
import SearchUsers from 'containers/pages/settings/search-users/SearchUsers';
import { Panel } from 'components/panel';

const Settings = () => {
  const {
    authStore: {
      user,
      user: { rootEntityId }
    }
  } = useContext(StoreContext);
  const intl = useIntl();
  const ability = useAbility(AbilityContext);
  const entityStore = useRef(createEntityStore()).current;

  if (ability.cannot('manage', 'Settings')) {
    return (
      <NoAccessPanel
        entityId={user.rootEntityId}
        emptyLabel={intl.formatMessage(messages.noAccessEmptyPanel)}
        redirectRoute={generatePath(ROUTE_PERSONS_ENTITY, {
          entityId: user.rootEntityId
        })}
      />
    );
  }

  return (
    <Fragment>
      <EntityStoreProvider value={entityStore}>
        <PreviewModalProvider entityId={rootEntityId}>
          <TestsProvider entityId={rootEntityId}>
            <TestSetContextProvider entityId={rootEntityId}>
              <Switch>
                <Route exact path={ROUTE_SETTINGS_ENTITY}>
                  <Overview />
                </Route>
                <Route path={ROUTE_SETTINGS_SUB}>
                  <Overview />
                </Route>
                <Route
                  path={ROUTE_SETTINGS}
                  render={() => (
                    <Redirect
                      to={generatePath(ROUTE_SETTINGS_ENTITY, {
                        entityId: user.rootEntityId
                      })}
                    />
                  )}
                />
              </Switch>
              <Route exact path={ROUTE_SETTINGS_ENTITY}>
                <Panel
                  emptyState
                  emptyLabel={intl.formatMessage(messages.usersEmptyPanel)}
                />
              </Route>
              <Switch>
                <Route exact path={ROUTE_SETTINGS_USERS}>
                  <Users />
                </Route>
                {ability.can('read', 'Organisations') && (
                  <Route exact path={ROUTE_SETTINGS_ORGANISATIONS}>
                    <Organisations />
                  </Route>
                )}
                {ability.can('read', 'Benchmarks') && (
                  <Route exact path={ROUTE_SETTINGS_BENCHMARKS}>
                    <Benchmarks />
                  </Route>
                )}
                {ability.can('read', 'Testsets') && (
                  <Route exact path={ROUTE_SETTINGS_TESTSETS}>
                    <TestSets />
                  </Route>
                )}
                {ability.can('read', 'Organisation') && (
                  <Route path={ROUTE_SETTINGS_GLOBAL_EDIT}>
                    <GlobalSettings edit />
                  </Route>
                )}
                {ability.can('read', 'Organisation') && (
                  <Route exact path={ROUTE_SETTINGS_GLOBAL}>
                    <GlobalSettings />
                  </Route>
                )}
                <Route path={ROUTE_SETTINGS_USERS_USER}>
                  <Users />
                </Route>
                {ability.can('read', 'Organisations') && (
                  <Route path={ROUTE_SETTINGS_ORGANISATIONS_ORG}>
                    <Organisations />
                  </Route>
                )}
                {ability.can('manage', 'Benchmarks') && (
                  <Route path={ROUTE_SETTINGS_BENCHMARKS_BENCHMARK}>
                    <Benchmarks />
                  </Route>
                )}
                {ability.can('manage', 'Testsets') && (
                  <Route path={ROUTE_SETTINGS_TESTSETS_TESTSET}>
                    <TestSets />
                  </Route>
                )}
                {ability.can('manage', 'Organisation') && (
                  <Route path={ROUTE_SETTINGS_USAGE}>
                    <Usage />
                  </Route>
                )}
                {ability.can('manage', 'Organisation') && (
                  <Route path={ROUTE_SETTINGS_EXPORT_DATA}>
                    <ExportData />
                  </Route>
                )}
                {ability.can('manage', 'Organisation') && (
                  <Route path={ROUTE_SETTINGS_DEMODATA}>
                    <DemoData />
                  </Route>
                )}
                <Route path={ROUTE_SETTINGS_PROFILE_MFA}>
                  <MfaSetup />
                </Route>
                <Route
                  path={[ROUTE_SETTINGS_PROFILE_EDIT, ROUTE_SETTINGS_PROFILE]}
                >
                  <Profile entityId={rootEntityId} />
                </Route>
                <Route path={ROUTE_SETTINGS_SEARCH_USERS}>
                  <SearchUsers />
                </Route>
                <Route
                  path={ROUTE_SETTINGS}
                  render={() => (
                    <Redirect
                      to={generatePath(ROUTE_SETTINGS_ENTITY, {
                        entityId: user.rootEntityId
                      })}
                    />
                  )}
                />
              </Switch>
              <Switch>
                {ability.can('create', 'Users') && (
                  <Route path={ROUTE_SETTINGS_USERS_ADD}>
                    <User entityId={rootEntityId} tabIndex={0} edit />
                  </Route>
                )}
                {ability.can('create', 'Organisations') && (
                  <Route path={ROUTE_SETTINGS_ORGANISATIONS_ADD}>
                    <Organisation />
                  </Route>
                )}
                <Switch>
                  {ability.can('manage', 'Users') && (
                    <Route path={ROUTE_SETTINGS_USERS_USER_EDIT}>
                      <User entityId={rootEntityId} tabIndex={0} edit />
                    </Route>
                  )}
                  {ability.can('manage', 'Organisations') && (
                    <Route path={ROUTE_SETTINGS_ORGANISATIONS_ORG_EDIT}>
                      <Organisation edit />
                    </Route>
                  )}
                  <Route path={ROUTE_SETTINGS_USERS_USER_ROLES}>
                    <User entityId={rootEntityId} tabIndex={1} />
                  </Route>
                  <Route path={ROUTE_SETTINGS_USERS_USER}>
                    <User entityId={rootEntityId} tabIndex={0} />
                  </Route>
                  {ability.can('read', 'Organisations') && (
                    <Route path={ROUTE_SETTINGS_ORGANISATIONS_ORG}>
                      <Organisation />
                    </Route>
                  )}
                  {ability.can('read', 'Benchmarks') && (
                    <Route exact path={ROUTE_SETTINGS_BENCHMARKS_BENCHMARK}>
                      <Benchmark />
                    </Route>
                  )}
                  {ability.can('read', 'Testsets') && (
                    <Route exact path={ROUTE_SETTINGS_TESTSETS_TESTSET}>
                      <TestSet />
                    </Route>
                  )}
                  {ability.can('read', 'Benchmarks') && (
                    <Route path={ROUTE_SETTINGS_BENCHMARKS_BENCHMARK_TEST}>
                      <Benchmark />
                    </Route>
                  )}
                </Switch>
              </Switch>
              <Switch>
                {ability.can('update', 'Benchmarks') && (
                  <Route path={ROUTE_SETTINGS_BENCHMARKS_BENCHMARK_TEST_EDIT}>
                    <BenchmarkTest edit />
                  </Route>
                )}
                {ability.can('read', 'Benchmarks') && (
                  <Route path={ROUTE_SETTINGS_BENCHMARKS_BENCHMARK_TEST}>
                    <BenchmarkTest />
                  </Route>
                )}
              </Switch>
            </TestSetContextProvider>
          </TestsProvider>
        </PreviewModalProvider>
      </EntityStoreProvider>
    </Fragment>
  );
};

export default observer(Settings);
